/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack } from '@mui/system';
import {
  Collapse,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { endOfDay, format, isValid, parse, startOfDay } from 'date-fns';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { TextField as SpectrumTextField } from '@bestdoctor/spectrum-uikit/TextField';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';
import { Checkbox as SpectrumCheckbox } from '@bestdoctor/spectrum-uikit/Checkbox';
import { ActionStaffModal } from 'shared/modals/ActionStaffModal/ActionStaffModal';
import { dialogStyleAddModal } from 'ui/modals/modalAdd/modalAdd';
import {
  modalHeaderStyle,
  modalTitleStyle,
  modalSubtitleStyle,
  modalFormStyle,
  modalFormScrollStyle,
  modalFormWrapStyle,
  modalFormWrapInputsStyle,
  modalFormInputStyle,
} from 'ui/modals/modalGlobal';
import { themeColor, grayLightDarkOpacity } from 'utils/uiConstants';
import { modalAddFromFileContent, optionsGender } from 'utils/constants';
import { useGetTaskActionStaff } from 'hooks/other/useGetTaskActionStaff';
import { useSearchPrograms } from 'hooks/programs/useSearchProgram';
import { useGetServicePeriods } from 'hooks/periods/useGetServicePeriods';
import { useAddPatientFromFile } from 'hooks/patients/useAddPatientFromFile';
import { useAddPatient } from 'hooks/patients/useAddPatient';
import { useSnackbar } from 'notistack';
import { IAddPatientModalProps } from 'interfaces/patients/patientInterfaces';
import { requiredPhoneByCompanies, requiredPassByCompanies } from 'utils/requiredCheck';
import { AddPatientHookResponseError, TaskId } from 'interfaces/hooks/patientsHooksInterfaces';
import { useGetPeriodStaffAction } from 'hooks/periods/useGetPeriodStaffAction';
import { TooltipActionsInfo } from 'ui/tooltips/tooltipAction';
import { useSearchRelativeSlots } from 'hooks/patients/useSearchRelativeSlots';
import { useDebounce } from 'utils/useDebounce';
import { isAccessAllowed } from 'utils/isAccessAllowed';
import { Company, ErrorData } from 'interfaces/global/globalInterfaces';
import { disabledDates } from 'utils/dateHelpers';
import { analyticsTracker } from 'utils/analytics';
import { isActive } from 'itson/ItsOn';
import { mapProgramsToSelectOptions } from 'utils/mapProgramsToSelectOptions';
import { validationPatient } from './validationAddPatient';
import { ActionWithPatientFromFileModal } from '../ActionWithPatientFromFileModal/ActionWithPatientFromFileModal';
import { GetTooltipTitleRelativeType } from './GetTooltipTitleRelativeType';
import { CrossingByCompany } from '../CrossingByCompany/CrossingByCompany';
import { CrossingByCompanyPeriod } from '../CrossingByCompanyPeriod/CrossingByCompanyPeriod';

export const AddPatientModal: React.FC<IAddPatientModalProps> = ({
  open,
  companiesHR,
  selectedCompanies,
  periodInMainFilter,
  isAddingForbiddenByDates,
  handleClose,
  handleTaskIdAttach,
  celeryType,
  accessList,
  defaultPermissions,
}) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalAttachFromFileOpen, setModalAttachFromFileOpen] = useState<boolean>(false);
  const [isCrossingByCompanyModalOpen, setCrossingByCompanyModalOpen] = useState<boolean>(false);
  const [isCrossingByCompanyPeriodModalOpen, setCrossingByCompanyPeriodModalOpen] = useState<boolean>(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  // id задачи по откреплению
  const [createSlotTaskId, setCreateSlotTaskId] = useState<string | null>(null);
  // стейты для дата пикеров (в формате dd.MM.yyyy для Spectrum)
  const [dateBirth, setDateBirth] = useState<string | undefined>(undefined);
  const [datePassport, setDatePassport] = useState<string | undefined>(undefined);
  const [dateStartProgram, setDateStartProgram] = useState<string | undefined>(undefined);
  const [selectedPatientType, setselectedPatientType] = useState<string>('');
  const [searchRelativeText, setSearchRelativeText] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const reqPhone: boolean = requiredPhoneByCompanies(companiesHR);
  const reqPass: boolean = requiredPassByCompanies(companiesHR);
  const validPatient = validationPatient(reqPhone, reqPass);
  type PatientParams = z.infer<typeof validPatient>;

  const isSlotsAviable = companiesHR
    ?.map((item: Company) => isAccessAllowed(item?.uuid, 'HR_slots', accessList, defaultPermissions))
    ?.some((key) => !!key);

  const { searchPatientsRelative } = useSearchRelativeSlots({
    fullname: searchRelativeText,
    holding: companiesHR[0]?.holding,
    isSlotsAviable,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const handleSearchTextRelativePatients = useCallback(
    (value: string): void => {
      setSearchRelativeText(value);
    },
    [setSearchRelativeText]
  );

  const handleSearchText = useDebounce(handleSearchTextRelativePatients, 1000);

  const { periodStaffAction } = useGetPeriodStaffAction({
    period: periodInMainFilter,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const disabledDays = (date: Date): boolean => {
    const actualDayTimeStamp = startOfDay(date).getTime();

    const canAttachSinceTimestamp = periodStaffAction
      ? startOfDay(parse(periodStaffAction.canAttachSince, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    const canAttachSinceTimestampMax = periodStaffAction
      ? endOfDay(parse(periodStaffAction.canAttachOrDetachMaxDate, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    if (disabledDates.some((item) => item === date.getTime())) {
      return disabledDates.some((item) => item === date.getTime());
    }
    if (canAttachSinceTimestamp && canAttachSinceTimestampMax) {
      return actualDayTimeStamp < canAttachSinceTimestamp || actualDayTimeStamp > canAttachSinceTimestampMax;
    }
    if (canAttachSinceTimestamp) {
      return actualDayTimeStamp < canAttachSinceTimestamp;
    }
    if (canAttachSinceTimestampMax) {
      return actualDayTimeStamp > canAttachSinceTimestampMax;
    }
    return false;
  };

  const [noMiddleName, setNoMiddleName] = useState(false);
  const noMiddleNameRef = useRef(false);
  noMiddleNameRef.current = noMiddleName;

  const {
    setValue,
    register,
    reset,
    watch,
    control,
    formState: { errors, isDirty, dirtyFields },
    setError,
    clearErrors,
    trigger,
    handleSubmit,
  } = useForm<PatientParams>({
    mode: 'onTouched',
    resolver: (values, context, options) => {
      const schema = validationPatient(reqPhone, reqPass, noMiddleNameRef.current);
      return zodResolver(schema)(values, context, options);
    },
    defaultValues: {
      last_name: '',
      first_name: '',
      middle_name: '',
      gender: '',
      birth_date: '',
      nationality: '',
      address: '',
      phone: null,
      email: '',
      position: '',
      number: '',
      passport_number: null,
      passport_date: null,
      passport_given: null,
      program: '',
      start_date: '',
      additional_info: '',
    },
  });

  const { dataServicePeriods } = useGetServicePeriods({
    companies: selectedCompanies,
    onSuccess: () => {},
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { isLoadingSearchPrograms, programs: searchPrograms } = useSearchPrograms({
    isProgramsAllowed: isSlotsAviable,
    company: selectedCompanies[0],
    period: periodInMainFilter,
    periods: dataServicePeriods,
    excludeEndedPeriodPrograms: true,
    selectedProgram: null,
    selectedServices: [],
    selectedСity: null,
    fetchAllPages: true,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const programs = useMemo(
    () => mapProgramsToSelectOptions(searchPrograms, { withBudget: true }),
    [searchPrograms]
  );

  const modalClose = (): void => {
    handleSearchText('');
    setselectedPatientType('');
    setDateBirth(undefined);
    setDatePassport(undefined);
    setDateStartProgram(undefined);
    reset();
    handleTaskIdAttach('');
    setCrossingByCompanyPeriodModalOpen(false);
    setCrossingByCompanyModalOpen(false);
    handleClose();
  };

  const { isLoadingAddPatient, handleAddPatient } = useAddPatient({
    dirtyFields,
    onSuccess: async (res, variables) => {
      if (res.ok) {
        if (!isActive('METASHARKSHR-668:trust_backend_on_patient_adding_date_intersection_check')) {
          const { data: responseData } = (await res.json()) as { data: { start_date: string } };
          const startDayIsDifferent =
            new Date(responseData.start_date.split('T')[0]).getTime() !==
            new Date(variables.start_date ?? '').getTime();

          if (startDayIsDifferent) {
            enqueueSnackbar(
              'Невозможно прикрепить пациента, у которого есть активный период обслуживания. Дождитесь окончания периода или обратитесь к Аккаунт менеджеру',
              { variant: 'error' }
            );

            modalClose();
            return;
          }
        }

        enqueueSnackbar('Пациент будет прикреплен в ближайшее время', { variant: 'success' });
        analyticsTracker.trackAttachmentFormSuccess();
        queryClient.invalidateQueries('dataCompanyPatients');

        modalClose();
        return;
      }

      if (res.status === 500) {
        enqueueSnackbar(
          'При прикреплении пациента произошла неизвестная ошибка. Пожалуйста, попробуйте позже или свяжитесь с аккаунт-менеджером.',
          { variant: 'error' }
        );
      }

      res.json().then((data: AddPatientHookResponseError) => {
        data.errors.forEach((item) => {
          const { field } = item;
          setError(field, {
            type: 'custom',
            message: item.message,
          });
          if (item.message === 'Диалог: Пациент прикреплен в другой компании в этот же период. Прикрепить?') {
            setCrossingByCompanyPeriodModalOpen(true);
          } else if (
            item.message === 'Диалог: Пациент пересекается в рамках компании-срока обслуживания. Прикрепить?'
          ) {
            setCrossingByCompanyModalOpen(true);
          } else {
            enqueueSnackbar(item.message, { variant: 'error' });
          }
        });
      });
    },
    onError: (res) => {
      if (res.status === 500) {
        enqueueSnackbar(
          'При прикреплении пациента произошла неизвестная ошибка. Пожалуйста, попробуйте позже или свяжитесь с аккаунт-менеджером.',
          { variant: 'error' }
        );
      }

      res.json().then((data: AddPatientHookResponseError) => {
        data.errors.forEach((item) => {
          const { field } = item;
          setError(field, {
            type: 'custom',
            message: item.message,
          });
          if (item.message === 'Диалог: Пациент прикреплен в другой компании в этот же период. Прикрепить?') {
            setCrossingByCompanyPeriodModalOpen(true);
          } else if (
            item.message === 'Диалог: Пациент пересекается в рамках компании-срока обслуживания. Прикрепить?'
          ) {
            setCrossingByCompanyModalOpen(true);
          } else {
            enqueueSnackbar(item.message, { variant: 'error' });
          }
        });
      });
    },
  });
  const { handleAddFromFile } = useAddPatientFromFile({
    companyId: selectedCompanies?.[0]?.uuid || '',
    onSuccess: (data: TaskId) => {
      setCreateSlotTaskId(data.data.task_id);
      setIsModalOpen(true);
    },
    onError: () => enqueueSnackbar('Ошибка при отправке заявки на прикрепление', { variant: 'error' }),
  });
  const { dataTaskActionStaff, refetchTaskActionStaff } = useGetTaskActionStaff({
    taskId: createSlotTaskId,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const isTariffError =
    periodInMainFilter?.tariff?.map((item) => item.value).indexOf('questioning') !== -1 &&
    periodInMainFilter?.relativesInsuranceType === 'corporate_agreement';

  const handleChangeAddFromFile = async (evt: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    evt.preventDefault();
    const fileList: FileList | null = evt.target.files ? evt.target.files : null;
    const file: File | null = fileList !== null ? fileList[0] : null;
    const formData: FormData = new FormData();
    if (file) {
      formData.append('file', file);

      analyticsTracker.trackAttachmentExcelAttempt();

      await handleAddFromFile(formData);

      analyticsTracker.trackAttachmentExcelSuccess();
    } else {
      enqueueSnackbar('Ошибка загрузки файла', { variant: 'error' });
      handleTaskIdAttach('');
    }
  };
  const refetch = (): void => {
    refetchTaskActionStaff();
  };

  const handleAttachClick = (): void => {
    analyticsTracker.trackAttachmentFormAttempt();
  };

  useEffect((): any => {
    if (isModalOpen && dataTaskActionStaff?.data.status !== 'success') {
      const interval = setInterval(() => {
        refetch();
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [isModalOpen, dataTaskActionStaff]);

  const handleCloseConfirmation = (): void => {
    setIsConfirmationOpen(false);
  };

  const handleConfirmClose = (): void => {
    handleCloseConfirmation();
    modalClose();
  };

  const handleModalClose = (): void => {
    if (isDirty) {
      setIsConfirmationOpen(true);
    } else {
      modalClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleModalClose} sx={dialogStyleAddModal}>
        <Box sx={modalHeaderStyle}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="h3" sx={modalTitleStyle}>
              Прикрепление нового пациента
            </Typography>
            <Typography variant="subtitle1" sx={modalSubtitleStyle}>
              Пользователь будет подключен к программе в самое ближайшее время!
              <br />
              Заполните анкету, чтобы прикрепить нового пациента, либо воспользуйтесь инструментом загрузки из файла
              (возможна загрузка сразу нескольких пациентов)
            </Typography>
          </Box>
          <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={handleModalClose}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={() => modalFormScrollStyle(600)}>
          <Box component="form" id="addingForm" sx={modalFormStyle} onSubmit={handleSubmit(handleAddPatient)}>
            <FormControl>
              <Typography variant="h4">Выберите категорию прикрепляемого пациента</Typography>
              <RadioGroup
                sx={{
                  padding: '19px 0 19px',
                }}
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={selectedPatientType}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setValue('patient_type', (event.target as HTMLInputElement).value, { shouldDirty: true });
                  register('patient_type', { value: (event.target as HTMLInputElement).value });

                  setselectedPatientType((event.target as HTMLInputElement).value);
                }}
              >
                <FormControlLabel
                  value="employee"
                  control={
                    <Radio
                      onChange={(item) => {
                        setselectedPatientType(item.target.value);
                      }}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '18px',
                      }}
                    >
                      Сотрудник
                    </Typography>
                  }
                />
                <TooltipActionsInfo
                  placement="right"
                  title={
                    periodInMainFilter?.relativesInsuranceType === 'individual_agreement' ||
                    periodInMainFilter?.relativesInsuranceType === 'disabled' ||
                    (periodInMainFilter?.relativesInsuranceType === 'corporate_agreement' && isTariffError) ? (
                      <GetTooltipTitleRelativeType
                        relativeType={periodInMainFilter?.relativesInsuranceType}
                        isTariffError={isTariffError}
                      />
                    ) : (
                      ''
                    )
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      width: 'max-content',
                    }}
                  >
                    <FormControlLabel
                      value="relative"
                      control={
                        <Radio
                          disabled={
                            periodInMainFilter?.relativesInsuranceType === 'disabled' ||
                            periodInMainFilter?.relativesInsuranceType === 'individual_agreement' ||
                            (periodInMainFilter?.relativesInsuranceType === 'corporate_agreement' && isTariffError)
                          }
                          onChange={(item) => {
                            setselectedPatientType(item.target.value);
                          }}
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 20,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '18px',
                          }}
                        >
                          Родственник
                        </Typography>
                      }
                    />
                  </Box>
                </TooltipActionsInfo>
              </RadioGroup>
            </FormControl>
            <Collapse in={selectedPatientType === 'employee' || selectedPatientType === 'relative'}>
              <Typography
                variant="h4"
                sx={{
                  marginBottom: '20px',
                }}
              >
                Укажите данные пациента
              </Typography>
              <Box>
                <Box sx={{ ...modalFormWrapStyle(false), marginBottom: 0 }}>
                  <Box sx={{ ...modalFormWrapInputsStyle, marginBottom: 0 }}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="last_name"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Фамилия"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('last_name', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.last_name?.message}
                            helperProps={{
                              hint: errors.last_name?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            required
                            disabled={isLoadingAddPatient}
                            size="m"
                            rightElement={
                              value ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('last_name', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                    </Box>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="first_name"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Имя"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('first_name', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.first_name?.message}
                            helperProps={{
                              hint: errors.first_name?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            required
                            disabled={isLoadingAddPatient}
                            size="m"
                            rightElement={
                              value ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('first_name', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...modalFormWrapInputsStyle, marginBottom: 0 }}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="middle_name"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Отчество"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('middle_name', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.middle_name?.message}
                            helperProps={{
                              hint: errors.middle_name?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            required={!noMiddleName}
                            disabled={isLoadingAddPatient || noMiddleName}
                            size="m"
                            rightElement={
                              value && !noMiddleName ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('middle_name', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                      <Box sx={{ marginTop: '6px' }}>
                        <SpectrumCheckbox
                          checked={noMiddleName}
                          onChange={(e) => {
                            const checked = (e.target as HTMLInputElement).checked;
                            setNoMiddleName(checked);
                            noMiddleNameRef.current = checked;
                            if (checked) {
                              setValue('middle_name', '', { shouldDirty: true });
                              clearErrors('middle_name');
                            } else {
                              trigger('middle_name');
                            }
                          }}
                          label="Нет отчества"
                        />
                      </Box>
                    </Box>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <style>
                        {`
                          [data-radix-popper-content-wrapper] {
                            z-index: 1400 !important;
                          }
                        `}
                      </style>
                      <Controller
                        control={control}
                        name="gender"
                        render={({ field: { value, onChange } }) => {
                          const genderOptions: ComboboxOption<string>[] = (optionsGender || []).map((gender) => ({
                            label: gender.title,
                            value: gender.value,
                          }));
                          
                          const selectedGenderOption = value
                            ? genderOptions.find((option) => option.value === value)
                            : undefined;
                          
                          return (
                            <Combobox<string>
                              label="Пол"
                              placeholder="Пол"
                              options={genderOptions}
                              value={selectedGenderOption?.value}
                              searchable
                              clearable
                              size="m"
                              required
                              disabled={isLoadingAddPatient}
                              onChange={(newValue, option) => {
                                if (newValue && option) {
                                  onChange(option.value);
                                  setValue('gender', option.value, { shouldDirty: true });
                                } else {
                                  onChange('');
                                  setValue('gender', '', { shouldDirty: true });
                                }
                              }}
                            />
                          );
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={modalFormWrapInputsStyle}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <style>
                        {`
                          [data-radix-popper-content-wrapper] {
                            z-index: 1400 !important;
                          }
                        `}
                      </style>
                      <Controller
                        control={control}
                        name="birth_date"
                        render={({ field: { value, onChange } }) => {
                          const displayValue = dateBirth || (value && value !== '' 
                            ? (() => {
                                try {
                                  const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
                                  if (isValid(parsedDate)) {
                                    return format(parsedDate, 'dd.MM.yyyy');
                                  }
                                } catch {
                                  // Если не удалось распарсить, возвращаем как есть
                                }
                                return value;
                              })()
                            : undefined);

                          return (
                            <DatePicker
                              mode="single"
                              label="Дата рождения"
                              placeholder="Дата рождения"
                              value={displayValue}
                              onChange={(newValue) => {
                                // Всегда сохраняем вводимую строку, чтобы разрешить ручной ввод
                                setDateBirth(newValue || undefined);
                                if (newValue) {
                                  try {
                                    const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                                    if (isValid(parsedDate)) {
                                      const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                                      onChange(formattedDate);
                                      setValue('birth_date', formattedDate, { shouldDirty: true });
                                    }
                                  } catch {
                                    // Если не удалось распарсить, игнорируем
                                  }
                                } else {
                                  onChange('');
                                  setValue('birth_date', '', { shouldDirty: true });
                                }
                              }}
                              disabled={isLoadingAddPatient}
                              error={!!errors.birth_date?.message}
                              helperProps={{
                                hint: errors.birth_date?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required
                              size="m"
                            />
                          );
                        }}
                      />
                    </Box>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="nationality"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Гражданство"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('nationality', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.nationality?.message}
                            helperProps={{
                              hint: errors.nationality?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            required
                            disabled={isLoadingAddPatient}
                            size="m"
                            rightElement={
                              value ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('nationality', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box sx={modalFormWrapInputsStyle}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="address"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Адрес фактического проживания"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('address', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.address?.message}
                            helperProps={{
                              hint: errors.address?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            required
                            disabled={isLoadingAddPatient}
                            size="m"
                            rightElement={
                              value ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('address', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Collapse in={selectedPatientType === 'relative'}>
                    <Box sx={modalFormWrapInputsStyle}>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <style>
                          {`
                            [data-radix-popper-content-wrapper] {
                              z-index: 1400 !important;
                            }
                          `}
                        </style>
                        <Controller
                          control={control}
                          name="relative_type"
                          render={({ field: { value, onChange } }) => {
                            const relativeTypeOptions: ComboboxOption<string>[] = (periodInMainFilter?.relativeCategories || []).map((category) => ({
                              label: category.title,
                              value: String(category.value),
                            }));
                            
                            const selectedRelativeTypeOption = value
                              ? relativeTypeOptions.find((option) => option.value === value)
                              : undefined;
                            
                            return (
                              <Combobox<string>
                                label="Кем приходится"
                                placeholder="Выберите связь"
                                options={relativeTypeOptions}
                                value={selectedRelativeTypeOption?.value}
                                searchable
                                clearable
                                size="m"
                                required={selectedPatientType === 'relative'}
                                disabled={isLoadingSearchPrograms || isLoadingAddPatient}
                                onChange={(newValue, option) => {
                                  if (newValue && option) {
                                    onChange(option.value);
                                    setValue('relative_type', option.value, { shouldDirty: true });
                                  } else {
                                    onChange('');
                                    setValue('relative_type', '', { shouldDirty: true });
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <style>
                          {`
                            [data-radix-popper-content-wrapper] {
                              z-index: 1400 !important;
                            }
                          `}
                        </style>
                        <Controller
                          control={control}
                          name="patient_relative"
                          render={({ field: { value, onChange } }) => {
                            const patientRelativeOptions: ComboboxOption<string>[] = (
                              searchPatientsRelative?.patients
                                ?.map((item) => ({
                                  label: `${item?.title} ${item?.date_birth}`,
                                  value: item?.uuid || '',
                                }))
                                .filter((item) => item.value && item.value !== watch('patient_relative_additional'))
                                .filter((item): item is ComboboxOption<string> => typeof item.value === 'string') || []
                            );
                            
                            const selectedPatientRelativeOption = value
                              ? patientRelativeOptions.find((option) => option.value === value)
                              : undefined;
                            
                            return (
                              <Combobox<string>
                                label="Кому приходится"
                                placeholder="Кому приходится"
                                options={patientRelativeOptions}
                                value={selectedPatientRelativeOption?.value}
                                searchable
                                clearable
                                size="m"
                                required={selectedPatientType === 'relative'}
                                disabled={isLoadingAddPatient}
                                onStateChange={(changes) => {
                                  if (typeof changes.inputValue === 'string') {
                                    handleSearchText(changes.inputValue);
                                  }
                                }}
                                onChange={(newValue, option) => {
                                  handleSearchText('');
                                  if (newValue && option) {
                                    onChange(option.value);
                                    setValue('patient_relative', option.value, { shouldDirty: true });
                                  } else {
                                    onChange('');
                                    setValue('patient_relative', '', { shouldDirty: true });
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                    </Box>
                    <Collapse in={watch('relative_type') === 'child'}>
                      <Box sx={modalFormWrapInputsStyle}>
                        <Box sx={() => modalFormInputStyle(false)}>
                          <style>
                            {`
                              [data-radix-popper-content-wrapper] {
                                z-index: 1400 !important;
                              }
                            `}
                          </style>
                          <Controller
                            control={control}
                            name="relative_type_additional"
                            render={({ field: { value, onChange } }) => {
                              const relativeTypeAdditionalOptions: ComboboxOption<string>[] = (periodInMainFilter?.relativeCategories || []).map((category) => ({
                                label: category.title,
                                value: String(category.value),
                              }));
                              
                              const selectedRelativeTypeAdditionalOption = value
                                ? relativeTypeAdditionalOptions.find((option) => option.value === value)
                                : undefined;
                              
                              return (
                                <Combobox<string>
                                  label="Кем приходится"
                                  placeholder="Выберите связь"
                                  options={relativeTypeAdditionalOptions}
                                  value={selectedRelativeTypeAdditionalOption?.value}
                                  searchable
                                  clearable
                                  size="m"
                                  disabled={isLoadingSearchPrograms || isLoadingAddPatient}
                                  onChange={(newValue, option) => {
                                    handleSearchText('');
                                    if (newValue && option) {
                                      onChange(option.value);
                                      setValue('relative_type_additional', option.value, { shouldDirty: true });
                                    } else {
                                      onChange('');
                                      setValue('relative_type_additional', '', { shouldDirty: true });
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Box sx={() => modalFormInputStyle(false)}>
                          <style>
                            {`
                              [data-radix-popper-content-wrapper] {
                                z-index: 1400 !important;
                              }
                            `}
                          </style>
                          <Controller
                            control={control}
                            name="patient_relative_additional"
                            render={({ field: { value, onChange } }) => {
                              const patientRelativeAdditionalOptions: ComboboxOption<string>[] = (
                                searchPatientsRelative?.patients
                                  ?.map((item) => ({
                                    label: `${item?.title} ${item?.date_birth}`,
                                    value: item?.uuid || '',
                                  }))
                                  .filter((item) => item.value && item.value !== watch('patient_relative'))
                                  .filter((item): item is ComboboxOption<string> => typeof item.value === 'string') || []
                              );
                              
                              const selectedPatientRelativeAdditionalOption = value
                                ? patientRelativeAdditionalOptions.find((option) => option.value === value)
                                : undefined;
                              
                              return (
                                <Combobox<string>
                                  label="Кому приходится"
                                  placeholder="Кому приходится"
                                  options={patientRelativeAdditionalOptions}
                                  value={selectedPatientRelativeAdditionalOption?.value}
                                  searchable
                                  clearable
                                  size="m"
                                  disabled={isLoadingAddPatient}
                                  onStateChange={(changes) => {
                                    if (typeof changes.inputValue === 'string') {
                                      handleSearchText(changes.inputValue);
                                    }
                                  }}
                                  onChange={(newValue, option) => {
                                    handleSearchText('');
                                    if (newValue && option) {
                                      onChange(option.value);
                                      setValue('patient_relative_additional', option.value, { shouldDirty: true });
                                    } else {
                                      onChange('');
                                      setValue('patient_relative_additional', '', { shouldDirty: true });
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                        </Box>
                      </Box>
                    </Collapse>
                  </Collapse>
                  <Box sx={() => modalFormWrapStyle(false)}>
                    <Box sx={modalFormWrapInputsStyle}>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="phone"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="Телефон"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('phone', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.phone?.message}
                              helperProps={{
                                hint: errors.phone?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required={reqPhone}
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('phone', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="email"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="E-mail"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('email', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.email?.message}
                              helperProps={{
                                hint: errors.email?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('email', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                    <Box sx={modalFormWrapInputsStyle}>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="position"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="Должность"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('position', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.position?.message}
                              helperProps={{
                                hint: errors.position?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('position', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="number"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="Табельный номер"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('number', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.number?.message}
                              helperProps={{
                                hint: errors.number?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('number', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                    <Box sx={modalFormWrapInputsStyle}>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="passport_number"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="Номер паспорта"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('passport_number', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.passport_number?.message}
                              helperProps={{
                                hint: errors.passport_number?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required={reqPass}
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('passport_number', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <style>
                          {`
                            [data-radix-popper-content-wrapper] {
                              z-index: 1400 !important;
                            }
                          `}
                        </style>
                        <Controller
                          control={control}
                          name="passport_date"
                          render={({ field: { value, onChange } }) => {
                            const displayValue = datePassport || (value && value !== '' 
                              ? (() => {
                                  try {
                                    const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
                                    if (isValid(parsedDate)) {
                                      return format(parsedDate, 'dd.MM.yyyy');
                                    }
                                  } catch {
                                    // Если не удалось распарсить, возвращаем как есть
                                  }
                                  return value;
                                })()
                              : undefined);

                            return (
                              <DatePicker
                                mode="single"
                                label="Дата выдачи"
                                placeholder="Дата выдачи"
                                value={displayValue}
                                onChange={(newValue) => {
                                  // Всегда сохраняем вводимую строку, чтобы разрешить ручной ввод
                                  setDatePassport(newValue || undefined);
                                  if (newValue) {
                                    try {
                                      const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                                      if (isValid(parsedDate)) {
                                        const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                                        onChange(formattedDate);
                                        setValue('passport_date', formattedDate, { shouldDirty: true });
                                      }
                                    } catch {
                                      // Если не удалось распарсить, игнорируем
                                    }
                                  } else {
                                    onChange('');
                                    setValue('passport_date', '', { shouldDirty: true });
                                  }
                                }}
                                disabled={isLoadingAddPatient}
                                error={!!errors.passport_date?.message}
                                helperProps={{
                                  hint: errors.passport_date?.message || '',
                                  textColor: {
                                    surface: 'light',
                                    variant: 'secondary'
                                  }
                                }}
                                required={reqPass}
                                size="m"
                              />
                            );
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={modalFormWrapInputsStyle}>
                      <Box sx={() => modalFormInputStyle(false)}>
                        <Controller
                          control={control}
                          name="passport_given"
                          render={({ field: { value, onChange, onBlur } }) => (
                            <SpectrumTextField
                              label="Кем выдан"
                              value={value || ''}
                              onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                onChange(newValue);
                                setValue('passport_given', newValue, { shouldDirty: true });
                              }}
                              onBlur={onBlur}
                              error={!!errors.passport_given?.message}
                              helperProps={{
                                hint: errors.passport_given?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required={reqPass}
                              disabled={isLoadingAddPatient}
                              size="m"
                              rightElement={
                                value ? (
                                  <IconButton
                                    sx={{ width: '28px', height: '28px', padding: 0 }}
                                    disabled={isLoadingAddPatient}
                                    onClick={() => {
                                      setValue('passport_given', '', { shouldDirty: true });
                                    }}
                                  >
                                    <CloseOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box sx={() => modalFormWrapStyle(false)}>
                  <Box sx={modalFormWrapInputsStyle}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <style>
                        {`
                          [data-radix-popper-content-wrapper] {
                            z-index: 1400 !important;
                          }
                        `}
                      </style>
                      <Controller
                        control={control}
                        name="program"
                        render={({ field: { value, onChange } }) => {
                          const filteredPrograms = programs && selectedPatientType
                            ? programs.filter(
                                (type) =>
                                  (type.allowedSlotPatientTypes &&
                                    type.allowedSlotPatientTypes?.includes('undefined')) ||
                                  type.allowedSlotPatientTypes?.includes(selectedPatientType)
                              )
                            : [];
                          
                          const programOptions: ComboboxOption<string>[] = filteredPrograms.map((program) => ({
                            label: program.title,
                            value: program.uuid,
                          }));
                          
                          const selectedProgramOption = value
                            ? programOptions.find((option) => option.value === value)
                            : undefined;
                          
                          return (
                            <Combobox<string>
                              label="Выберите программу"
                              placeholder="Выберите программу"
                              options={programOptions}
                              value={selectedProgramOption?.value}
                              searchable
                              clearable
                              size="m"
                              required
                              disabled={isLoadingSearchPrograms || isLoadingAddPatient}
                              onChange={(newValue, option) => {
                                if (newValue && option) {
                                  onChange(option.value);
                                  setValue('program', option.value, { shouldDirty: true });
                                } else {
                                  onChange('');
                                  setValue('program', '', { shouldDirty: true });
                                }
                              }}
                            />
                          );
                        }}
                      />
                    </Box>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <style>
                        {`
                          [data-radix-popper-content-wrapper] {
                            z-index: 1400 !important;
                          }
                        `}
                      </style>
                      <Controller
                        control={control}
                        name="start_date"
                        render={({ field: { value, onChange } }) => {
                          const displayValue = dateStartProgram || (value && value !== '' 
                            ? (() => {
                                try {
                                  const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
                                  if (isValid(parsedDate)) {
                                    return format(parsedDate, 'dd.MM.yyyy');
                                  }
                                } catch {
                                  // Если не удалось распарсить, возвращаем как есть
                                }
                                return value;
                              })()
                            : undefined);

                          return (
                            <DatePicker
                              mode="single"
                              label="Дата начала обслуживания"
                              placeholder="Дата начала обслуживания"
                              value={displayValue}
                              onChange={(newValue) => {
                                // Всегда сохраняем вводимую строку, чтобы разрешить ручной ввод
                                setDateStartProgram(newValue || undefined);
                                if (newValue) {
                                  try {
                                    const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                                    if (isValid(parsedDate)) {
                                      if (!disabledDays(parsedDate)) {
                                        const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                                        onChange(formattedDate);
                                        setValue('start_date', formattedDate, { shouldDirty: true });
                                      } else {
                                        setError('start_date', {
                                          type: 'custom',
                                          message: 'Выбранная дата недоступна',
                                        });
                                      }
                                    }
                                  } catch {
                                    // Если не удалось распарсить, игнорируем
                                  }
                                } else {
                                  onChange('');
                                  setValue('start_date', '', { shouldDirty: true });
                                }
                              }}
                              disabled={isLoadingSearchPrograms || isLoadingAddPatient}
                              error={!!errors.start_date?.message}
                              helperProps={{
                                hint: errors.start_date?.message || '',
                                textColor: {
                                  surface: 'light',
                                  variant: 'secondary'
                                }
                              }}
                              required
                              size="m"
                              calendarProps={{
                                disabled: (date: Date) => disabledDays(date),
                              }}
                            />
                          );
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={modalFormWrapInputsStyle}>
                    <Box sx={() => modalFormInputStyle(false)}>
                      <Controller
                        control={control}
                        name="additional_info"
                        render={({ field: { value, onChange, onBlur } }) => (
                          <SpectrumTextField
                            label="Доп. информация"
                            value={value || ''}
                            onChange={(e) => {
                              const newValue = (e.target as HTMLInputElement).value;
                              onChange(newValue);
                              setValue('additional_info', newValue, { shouldDirty: true });
                            }}
                            onBlur={onBlur}
                            error={!!errors.additional_info?.message}
                            helperProps={{
                              hint: errors.additional_info?.message || '',
                              textColor: {
                                surface: 'light',
                                variant: 'secondary'
                              }
                            }}
                            disabled={isLoadingAddPatient}
                            size="m"
                            rightElement={
                              value ? (
                                <IconButton
                                  sx={{ width: '28px', height: '28px', padding: 0 }}
                                  disabled={isLoadingAddPatient}
                                  onClick={() => {
                                    setValue('additional_info', '', { shouldDirty: true });
                                  }}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : undefined
                            }
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            width: '100%',
            padding: '8px 16px 16px',
          }}
        >
          <Typography variant="caption" sx={{ color: grayLightDarkOpacity }}>
            <Typography variant="caption" sx={{ color: themeColor }}>
              *{' '}
            </Typography>
            - обязательные поля.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '32px',
            }}
          >
            <Box>
              <SpectrumButton
                variant="pseudo"
                onClick={() => setModalAttachFromFileOpen(open)}
              >
                Прикрепить из файла
              </SpectrumButton>
            </Box>
            <TooltipActionsInfo
              placement="left"
              title={isAddingForbiddenByDates ? 'Нет доступных дат для прикрепления пациента' : ''}
            >
              <Box component="span">
                <SpectrumButton
                  variant="promo"
                  type="submit"
                  form="addingForm"
                  onClick={handleAttachClick}
                  disabled={isLoadingAddPatient || !isDirty || isAddingForbiddenByDates}
                >
                  Прикрепить
                </SpectrumButton>
              </Box>
            </TooltipActionsInfo>
          </Box>
        </Stack>
      </Dialog>

      <Dialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '24px',
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: '300px' }}>
          <Stack spacing={2}>
            <Typography variant="h3" sx={modalTitleStyle}>
              Предупреждение
            </Typography>
            <Typography variant="body1" id="alert-dialog-title" sx={modalSubtitleStyle}>
              Если вы закроете форму, введенные вами данные пациента будут очищены!
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <SpectrumButton
                variant="pseudo"
                onClick={handleCloseConfirmation}
              >
                Отмена
              </SpectrumButton>
              <SpectrumButton
                variant="promo"
                onClick={handleConfirmClose}
              >
                Закрыть
              </SpectrumButton>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      <ActionStaffModal
        celeryType={celeryType}
        textContent={modalAddFromFileContent}
        open={isModalOpen}
        displayErrors
        dataTaskActionStaff={dataTaskActionStaff?.data}
        handleClose={() => {
          setIsModalOpen(false);
          setModalAttachFromFileOpen(false);
          handleTaskIdAttach('');
          setCreateSlotTaskId(null);
          modalClose();
        }}
      />
      <ActionWithPatientFromFileModal
        status={dataTaskActionStaff?.data?.status.toLowerCase()}
        isActionMuted={isAddingForbiddenByDates}
        type="attach"
        companies={companiesHR}
        open={isModalAttachFromFileOpen}
        handleClose={() => setModalAttachFromFileOpen(false)}
        periodInMainFilter={periodInMainFilter}
        handleChangeAttachFromFile={handleChangeAddFromFile}
      />
      <CrossingByCompany
        open={isCrossingByCompanyModalOpen}
        handleClose={() => setCrossingByCompanyModalOpen(false)}
        handleCancel={() => setCrossingByCompanyModalOpen(false)}
        handleSubmit={() => handleAddPatient({ ...watch(), is_forced_attachment: true })}
      />
      <CrossingByCompanyPeriod
        open={isCrossingByCompanyPeriodModalOpen}
        handleClose={() => setCrossingByCompanyPeriodModalOpen(false)}
        handleCancel={() => setCrossingByCompanyPeriodModalOpen(false)}
        handleSubmit={() => handleAddPatient({ ...watch(), is_forced_attachment: true })}
      />
    </>
  );
};
